import React, { useMemo } from 'react';
import ReactFlow, { Background, Controls, MarkerType, Handle, Position } from 'reactflow';
import 'reactflow/dist/style.css';
import './DatabaseDiagram.css';

/**
 * DatabaseDiagram Component
 * Professional React Flow-based diagrams for database architecture
 */

// Custom Node Components with Handles
const AccountNode = ({ data }) => (
  <div className="rf-node account-node">
    <Handle type="source" position={Position.Bottom} style={{ background: 'var(--color-primary-light)' }} />
    <div className="node-header">{data.label}</div>
    <div className="node-subtitle">{data.subtitle}</div>
  </div>
);

const DatabaseNode = ({ data }) => (
  <div className="rf-node database-node">
    <Handle type="target" position={Position.Top} style={{ background: 'var(--color-primary-light)' }} />
    <Handle type="source" position={Position.Bottom} style={{ background: 'var(--color-primary-light)' }} />
    <div className="node-header">{data.label}</div>
    <div className="node-subtitle">{data.subtitle}</div>
    {data.containers && (
      <div className="containers-list">
        {data.containers.map((container, idx) => (
          <div key={idx} className="container-item">{container}</div>
        ))}
      </div>
    )}
  </div>
);

const StructureNode = ({ data }) => (
  <div className="rf-node structure-node">
    <Handle type="source" position={Position.Bottom} style={{ background: 'var(--color-primary-light)' }} />
    <div className="node-header">{data.label}</div>
    <div className="node-subtitle">{data.subtitle}</div>
  </div>
);

const ContainerNode = ({ data }) => (
  <div className="rf-node container-node">
    <Handle type="target" position={Position.Top} style={{ background: 'var(--color-primary-light)' }} />
    <Handle type="source" position={Position.Right} style={{ background: 'var(--color-primary-light)' }} />
    <div className="node-header">{data.label}</div>
  </div>
);

const KeyNode = ({ data }) => (
  <div className="rf-node key-node">
    <Handle type="target" position={Position.Left} style={{ background: 'var(--color-primary-light)' }} />
    <Handle type="source" position={Position.Right} style={{ background: 'var(--color-primary-light)' }} />
    <div className="node-header">{data.label}</div>
    <div className="node-subtitle">{data.subtitle}</div>
  </div>
);

const PartitionNode = ({ data }) => (
  <div className="rf-node partition-node">
    <Handle type="target" position={Position.Left} style={{ background: 'var(--color-primary-light)' }} />
    <div className="node-header">{data.label}</div>
    <div className="node-subtitle">{data.subtitle}</div>
  </div>
);

const RegionNode = ({ data }) => (
  <div className="rf-node region-node">
    <Handle type="source" position={Position.Bottom} style={{ background: 'var(--color-primary-light)' }} />
    <Handle type="target" position={Position.Top} style={{ background: 'var(--color-primary-light)' }} />
    <div className="node-header">{data.label}</div>
    <div className="node-subtitle">{data.subtitle}</div>
  </div>
);

const OpNode = ({ data }) => (
  <div className="rf-node op-node">
    <Handle type="target" position={Position.Top} style={{ background: 'var(--color-primary-light)' }} />
    <Handle type="source" position={Position.Bottom} style={{ background: 'var(--color-primary-light)' }} />
    <div className="node-header">{data.label}</div>
  </div>
);

const ConsistencyNode = ({ data }) => (
  <div className="rf-node consistency-node">
    <Handle type="target" position={Position.Top} style={{ background: 'var(--color-primary-light)' }} />
    <div className="node-header">{data.label}</div>
    <div className="node-subtitle">{data.subtitle}</div>
  </div>
);

const ProcessorNode = ({ data }) => (
  <div className="rf-node processor-node">
    <Handle type="target" position={Position.Top} style={{ background: 'var(--color-primary-light)' }} />
    <Handle type="source" position={Position.Bottom} style={{ background: 'var(--color-primary-light)' }} />
    <div className="node-header">{data.label}</div>
  </div>
);

const DownstreamNode = ({ data }) => (
  <div className="rf-node downstream-node">
    <Handle type="target" position={Position.Top} style={{ background: 'var(--color-primary-light)' }} />
    <div className="node-header">{data.label}</div>
  </div>
);

const StorageNode = ({ data }) => (
  <div className="rf-node storage-node">
    <Handle type="target" position={Position.Top} style={{ background: 'var(--color-primary-light)' }} />
    <div className="node-header">{data.label}</div>
    <div className="node-subtitle">{data.subtitle}</div>
  </div>
);

const RecoveryNode = ({ data }) => (
  <div className="rf-node recovery-node">
    <Handle type="target" position={Position.Top} style={{ background: 'var(--color-primary-light)' }} />
    <div className="node-header">{data.label}</div>
    {data.subtitle && <div className="node-subtitle">{data.subtitle}</div>}
  </div>
);

// Node types
const nodeTypes = {
  account: AccountNode,
  database: DatabaseNode,
  structure: StructureNode,
  container: ContainerNode,
  key: KeyNode,
  partition: PartitionNode,
  region: RegionNode,
  operation: OpNode,
  consistency: ConsistencyNode,
  processor: ProcessorNode,
  downstream: DownstreamNode,
  storage: StorageNode,
  recovery: RecoveryNode,
};

// Helper function to create professional edges
const createEdge = (id, source, target, sourceHandle, targetHandle, options = {}) => ({
  id,
  source,
  target,
  sourceHandle,
  targetHandle,
  animated: true,
  style: { stroke: 'var(--color-primary-light)', strokeWidth: 3, ...options.style },
  markerEnd: { type: MarkerType.ArrowClosed, color: 'var(--color-primary-light)', width: 20, height: 20 },
  ...options,
});

// Database Overview Diagram
export const DatabaseOverviewDiagram = () => {
  const { nodes, edges } = useMemo(() => {
    const nodes = [
      {
        id: 'account',
        type: 'account',
        position: { x: 400, y: 0 },
        data: { label: 'MongoDB Account', subtitle: 'Serverless Mode' },
      },
      {
        id: 'public-db',
        type: 'database',
        position: { x: 50, y: 180 },
        data: {
          label: 'Public Database',
          subtitle: 'Demo Resources',
          containers: ['resources', 'knowledge', 'sessions', 'public_data', 'public_information'],
        },
      },
      {
        id: 'besper-db',
        type: 'database',
        position: { x: 400, y: 180 },
        data: {
          label: 'Besper Database',
          subtitle: 'Multi-Tenant',
          containers: ['resources', 'knowledge', 'sessions', 'tenants', 'users', 'communication', 'cost_tracking', 'system_logs'],
        },
      },
      {
        id: 'admin-db',
        type: 'database',
        position: { x: 750, y: 180 },
        data: {
          label: 'Admin Database',
          subtitle: 'Deployment Data',
          containers: ['deployments', 'deployment_jobs'],
        },
      },
    ];

    const edges = [
      createEdge('e1', 'account', 'public-db', 'bottom', 'top'),
      createEdge('e2', 'account', 'besper-db', 'bottom', 'top'),
      createEdge('e3', 'account', 'admin-db', 'bottom', 'top'),
    ];

    return { nodes, edges };
  }, []);

  return (
    <div className="database-diagram rf-container">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        fitView
        minZoom={0.5}
        maxZoom={1.5}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        defaultEdgeOptions={{ animated: true, style: { stroke: 'var(--color-primary-light)', strokeWidth: 3 } }}
      >
        <Background color="#f0f0f0" gap={16} />
        <Controls showInteractive={false} />
      </ReactFlow>
    </div>
  );
};

// Data Model Diagram
export const DataModelDiagram = () => {
  const { nodes, edges } = useMemo(() => {
    const nodes = [
      { id: 'doc-id', type: 'structure', position: { x: 50, y: 0 }, data: { label: 'Document ID', subtitle: 'Unique Identifier' } },
      { id: 'partition-key', type: 'structure', position: { x: 250, y: 0 }, data: { label: 'Partition Key', subtitle: 'tenant_id / resource_id' } },
      { id: 'properties', type: 'structure', position: { x: 450, y: 0 }, data: { label: 'Properties', subtitle: 'JSON Fields' } },
      { id: 'metadata', type: 'structure', position: { x: 650, y: 0 }, data: { label: 'Metadata', subtitle: 'created_at, updated_at' } },
      { id: 'container-resources', type: 'container', position: { x: 150, y: 180 }, data: { label: 'Container: resources' } },
      { id: 'container-knowledge', type: 'container', position: { x: 350, y: 180 }, data: { label: 'Container: knowledge' } },
      { id: 'container-sessions', type: 'container', position: { x: 550, y: 180 }, data: { label: 'Container: sessions' } },
    ];

    const edges = [
      createEdge('e1', 'doc-id', 'container-resources', 'bottom', 'top'),
      createEdge('e2', 'doc-id', 'container-knowledge', 'bottom', 'top'),
      createEdge('e3', 'doc-id', 'container-sessions', 'bottom', 'top'),
      createEdge('e4', 'partition-key', 'container-resources', 'bottom', 'top'),
      createEdge('e5', 'partition-key', 'container-knowledge', 'bottom', 'top'),
      createEdge('e6', 'partition-key', 'container-sessions', 'bottom', 'top'),
      createEdge('e7', 'properties', 'container-resources', 'bottom', 'top'),
      createEdge('e8', 'properties', 'container-knowledge', 'bottom', 'top'),
      createEdge('e9', 'properties', 'container-sessions', 'bottom', 'top'),
      createEdge('e10', 'metadata', 'container-resources', 'bottom', 'top'),
      createEdge('e11', 'metadata', 'container-knowledge', 'bottom', 'top'),
      createEdge('e12', 'metadata', 'container-sessions', 'bottom', 'top'),
    ];

    return { nodes, edges };
  }, []);

  return (
    <div className="database-diagram rf-container">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        fitView
        minZoom={0.5}
        maxZoom={1.5}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        defaultEdgeOptions={{ animated: true, style: { stroke: 'var(--color-primary-light)', strokeWidth: 3 } }}
      >
        <Background color="#f0f0f0" gap={16} />
        <Controls showInteractive={false} />
      </ReactFlow>
    </div>
  );
};

// Partitioning Strategy Diagram
export const PartitioningDiagram = () => {
  const { nodes, edges } = useMemo(() => {
    const nodes = [
      { id: 'c1', type: 'container', position: { x: 0, y: 0 }, data: { label: 'Container: resources' } },
      { id: 'c2', type: 'container', position: { x: 0, y: 100 }, data: { label: 'Container: knowledge' } },
      { id: 'c3', type: 'container', position: { x: 0, y: 200 }, data: { label: 'Container: sessions' } },
      { id: 'c4', type: 'container', position: { x: 0, y: 300 }, data: { label: 'Container: communication' } },
      { id: 'k1', type: 'key', position: { x: 300, y: 0 }, data: { label: 'tenant_id', subtitle: 'Multi-tenant isolation' } },
      { id: 'k2', type: 'key', position: { x: 300, y: 100 }, data: { label: 'resource_id', subtitle: 'Resource-specific' } },
      { id: 'k3', type: 'key', position: { x: 300, y: 200 }, data: { label: 'data_type', subtitle: 'Session type' } },
      { id: 'k4', type: 'key', position: { x: 300, y: 300 }, data: { label: 'commType, tenantId, userId', subtitle: 'Hierarchical' } },
      { id: 'p1', type: 'partition', position: { x: 600, y: 0 }, data: { label: 'Partition 1', subtitle: 'Tenant A' } },
      { id: 'p2', type: 'partition', position: { x: 600, y: 100 }, data: { label: 'Partition 2', subtitle: 'Tenant B' } },
      { id: 'p3', type: 'partition', position: { x: 600, y: 200 }, data: { label: 'Partition 3', subtitle: 'Tenant C' } },
      { id: 'p4', type: 'partition', position: { x: 600, y: 300 }, data: { label: 'Partition N', subtitle: 'Tenant N' } },
    ];

    const edges = [
      createEdge('e1', 'c1', 'k1', 'right', 'left'),
      createEdge('e2', 'c2', 'k1', 'right', 'left'),
      createEdge('e3', 'c3', 'k3', 'right', 'left'),
      createEdge('e4', 'c4', 'k4', 'right', 'left'),
      createEdge('e5', 'k1', 'p1', 'right', 'left'),
      createEdge('e6', 'k1', 'p2', 'right', 'left'),
      createEdge('e7', 'k1', 'p3', 'right', 'left'),
      createEdge('e8', 'k1', 'p4', 'right', 'left'),
    ];

    return { nodes, edges };
  }, []);

  return (
    <div className="database-diagram rf-container">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        fitView
        minZoom={0.5}
        maxZoom={1.5}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        defaultEdgeOptions={{ animated: true, style: { stroke: 'var(--color-primary-light)', strokeWidth: 3 } }}
      >
        <Background color="#f0f0f0" gap={16} />
        <Controls showInteractive={false} />
      </ReactFlow>
    </div>
  );
};

// Consistency & Availability Diagram
export const ConsistencyDiagram = () => {
  const { nodes, edges } = useMemo(() => {
    const nodes = [
      { id: 'primary', type: 'region', position: { x: 300, y: 0 }, data: { label: 'MongoDB Account', subtitle: 'Germany West Central' } },
      { id: 'write', type: 'operation', position: { x: 100, y: 150 }, data: { label: 'Write Operations' } },
      { id: 'read', type: 'operation', position: { x: 500, y: 150 }, data: { label: 'Read Operations' } },
      { id: 'region2', type: 'region', position: { x: 0, y: 300 }, data: { label: 'Region 2', subtitle: 'Read Replica' } },
      { id: 'region3', type: 'region', position: { x: 300, y: 300 }, data: { label: 'Region 3', subtitle: 'Read Replica' } },
      { id: 'regionN', type: 'region', position: { x: 600, y: 300 }, data: { label: 'Region N', subtitle: 'Read Replica' } },
      { id: 'strong', type: 'consistency', position: { x: 0, y: 500 }, data: { label: 'Strong', subtitle: 'Immediate' } },
      { id: 'session', type: 'consistency', position: { x: 200, y: 500 }, data: { label: 'Session', subtitle: 'Default' } },
      { id: 'bounded', type: 'consistency', position: { x: 400, y: 500 }, data: { label: 'Bounded Staleness', subtitle: 'Read-Heavy' } },
      { id: 'eventual', type: 'consistency', position: { x: 600, y: 500 }, data: { label: 'Eventual', subtitle: 'Analytics' } },
    ];

    const edges = [
      createEdge('e1', 'primary', 'write', 'bottom', 'top'),
      createEdge('e2', 'primary', 'read', 'bottom', 'top'),
      createEdge('e3', 'primary', 'region2', 'bottom', 'top', { style: { stroke: 'var(--color-primary-light)', strokeWidth: 3, strokeDasharray: '5,5' } }),
      createEdge('e4', 'primary', 'region3', 'bottom', 'top', { style: { stroke: 'var(--color-primary-light)', strokeWidth: 3, strokeDasharray: '5,5' } }),
      createEdge('e5', 'primary', 'regionN', 'bottom', 'top', { style: { stroke: 'var(--color-primary-light)', strokeWidth: 3, strokeDasharray: '5,5' } }),
      createEdge('e6', 'write', 'strong', 'bottom', 'top'),
      createEdge('e7', 'read', 'session', 'bottom', 'top'),
      createEdge('e8', 'read', 'bounded', 'bottom', 'top'),
      createEdge('e9', 'read', 'eventual', 'bottom', 'top'),
    ];

    return { nodes, edges };
  }, []);

  return (
    <div className="database-diagram rf-container">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        fitView
        minZoom={0.5}
        maxZoom={1.5}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        defaultEdgeOptions={{ animated: true, style: { stroke: 'var(--color-primary-light)', strokeWidth: 3 } }}
      >
        <Background color="#f0f0f0" gap={16} />
        <Controls showInteractive={false} />
      </ReactFlow>
    </div>
  );
};

// Change Feed Diagram
export const ChangeFeedDiagram = () => {
  const { nodes, edges } = useMemo(() => {
    const nodes = [
      { id: 'container', type: 'container', position: { x: 300, y: 0 }, data: { label: 'MongoDB Container' } },
      { id: 'write', type: 'operation', position: { x: 100, y: 150 }, data: { label: 'Document Write' } },
      { id: 'feed', type: 'operation', position: { x: 500, y: 150 }, data: { label: 'Change Feed' } },
      { id: 'notif', type: 'processor', position: { x: 0, y: 300 }, data: { label: 'Notification Processor' } },
      { id: 'repl', type: 'processor', position: { x: 200, y: 300 }, data: { label: 'Replication Processor' } },
      { id: 'audit', type: 'processor', position: { x: 400, y: 300 }, data: { label: 'Audit Logger' } },
      { id: 'event', type: 'processor', position: { x: 600, y: 300 }, data: { label: 'Event Handler' } },
      { id: 'func', type: 'downstream', position: { x: 100, y: 500 }, data: { label: 'Azure Functions' } },
      { id: 'signalr', type: 'downstream', position: { x: 300, y: 500 }, data: { label: 'SignalR Hub' } },
      { id: 'queue', type: 'downstream', position: { x: 500, y: 500 }, data: { label: 'Storage Queue' } },
      { id: 'analytics', type: 'downstream', position: { x: 700, y: 500 }, data: { label: 'Analytics' } },
    ];

    const edges = [
      createEdge('e1', 'container', 'write', 'bottom', 'top'),
      createEdge('e2', 'container', 'feed', 'bottom', 'top'),
      createEdge('e3', 'feed', 'notif', 'bottom', 'top'),
      createEdge('e4', 'feed', 'repl', 'bottom', 'top'),
      createEdge('e5', 'feed', 'audit', 'bottom', 'top'),
      createEdge('e6', 'feed', 'event', 'bottom', 'top'),
      createEdge('e7', 'notif', 'signalr', 'bottom', 'top'),
      createEdge('e8', 'notif', 'queue', 'bottom', 'top'),
      createEdge('e9', 'repl', 'func', 'bottom', 'top'),
      createEdge('e10', 'audit', 'analytics', 'bottom', 'top'),
      createEdge('e11', 'event', 'func', 'bottom', 'top'),
    ];

    return { nodes, edges };
  }, []);

  return (
    <div className="database-diagram rf-container">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        fitView
        minZoom={0.5}
        maxZoom={1.5}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        defaultEdgeOptions={{ animated: true, style: { stroke: 'var(--color-primary-light)', strokeWidth: 3 } }}
      >
        <Background color="#f0f0f0" gap={16} />
        <Controls showInteractive={false} />
      </ReactFlow>
    </div>
  );
};

// Backup & Recovery Diagram
export const BackupDiagram = () => {
  const { nodes, edges } = useMemo(() => {
    const nodes = [
      { id: 'primary', type: 'region', position: { x: 300, y: 0 }, data: { label: 'MongoDB Account', subtitle: 'Active' } },
      { id: 'backup', type: 'operation', position: { x: 300, y: 150 }, data: { label: 'Continuous Backup', subtitle: 'Every 4 hours' } },
      { id: 'b1', type: 'storage', position: { x: 0, y: 300 }, data: { label: 'Backup 1', subtitle: 'Region 1' } },
      { id: 'b2', type: 'storage', position: { x: 200, y: 300 }, data: { label: 'Backup 2', subtitle: 'Region 2' } },
      { id: 'b3', type: 'storage', position: { x: 400, y: 300 }, data: { label: 'Backup 3', subtitle: 'Region 3' } },
      { id: 'pitr', type: 'storage', position: { x: 600, y: 300 }, data: { label: 'Point-in-Time', subtitle: 'Last 30 days' } },
      { id: 'r1', type: 'recovery', position: { x: 0, y: 500 }, data: { label: 'Point-in-Time Restore' } },
      { id: 'r2', type: 'recovery', position: { x: 200, y: 500 }, data: { label: 'Full Database Restore' } },
      { id: 'r3', type: 'recovery', position: { x: 400, y: 500 }, data: { label: 'Container Restore' } },
      { id: 'r4', type: 'recovery', position: { x: 600, y: 500 }, data: { label: 'Disaster Recovery', subtitle: 'Auto Failover' } },
    ];

    const edges = [
      createEdge('e1', 'primary', 'backup', 'bottom', 'top'),
      createEdge('e2', 'backup', 'b1', 'bottom', 'top'),
      createEdge('e3', 'backup', 'b2', 'bottom', 'top'),
      createEdge('e4', 'backup', 'b3', 'bottom', 'top'),
      createEdge('e5', 'backup', 'pitr', 'bottom', 'top'),
      createEdge('e6', 'b1', 'r1', 'bottom', 'top'),
      createEdge('e7', 'b2', 'r2', 'bottom', 'top'),
      createEdge('e8', 'b3', 'r3', 'bottom', 'top'),
      createEdge('e9', 'pitr', 'r1', 'bottom', 'top'),
      createEdge('e10', 'primary', 'r4', 'bottom', 'top', { style: { stroke: 'var(--color-primary-light)', strokeWidth: 3, strokeDasharray: '5,5' } }),
    ];

    return { nodes, edges };
  }, []);

  return (
    <div className="database-diagram rf-container">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        fitView
        minZoom={0.5}
        maxZoom={1.5}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        defaultEdgeOptions={{ animated: true, style: { stroke: 'var(--color-primary-light)', strokeWidth: 3 } }}
      >
        <Background color="#f0f0f0" gap={16} />
        <Controls showInteractive={false} />
      </ReactFlow>
    </div>
  );
};
